/* ============================================================================
   THE DOUX — COLLECTION GRID JS
   ----------------------------------------------------------------------------
   - Native Shopify Storefront Filtering (filter.v.*, filter.p.*)
   - URL state persistence (browser back/forward works)
   - Async section render via Section Rendering API
   - Quick Add → /cart/add.js → hot-swap #header-minicart-drawer
   - Quick View modal with variant selector
   - Cart drawer triggered by clicking [data-site-actions-cart]
   ============================================================================ */

(function () {
  'use strict';

  const root = document.querySelector('[data-collection-grid]');
  if (!root) return;

  const SECTION_ID = root.closest('[data-section-id]')?.dataset.sectionId
    || document.querySelector('[id^="shopify-section-"][id*="collection"]')?.id.replace('shopify-section-', '')
    || 'collection-product-grid';

  /* ============================================================
     STATE + URL
  ============================================================ */

  const getURL = () => new URL(window.location.href);

  const updateURL = (params, replace = false) => {
    const url = getURL();
    // Clear existing filter/sort params
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith('filter.') || key === 'sort_by') url.searchParams.delete(key);
    });
    params.forEach((value, key) => url.searchParams.append(key, value));
    if (replace) window.history.replaceState({}, '', url);
    else window.history.pushState({}, '', url);
  };

  /* ============================================================
     ASYNC SECTION RENDER
  ============================================================ */

  let fetchAbort = null;

  async function renderSection() {
    if (fetchAbort) fetchAbort.abort();
    fetchAbort = new AbortController();

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', SECTION_ID);

    root.setAttribute('aria-busy', 'true');
    root.style.opacity = '0.5';
    root.style.pointerEvents = 'none';

    try {
      const res = await fetch(url.toString(), { signal: fetchAbort.signal });
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newSection = parsed.querySelector('[data-collection-grid]');
      if (!newSection) return;

      // Replace grid, sidebar, count, active filters
      const swaps = [
        ['[data-product-grid]', '[data-product-grid]'],
        ['[data-filters-form]', '[data-filters-form]'],
        ['[data-active-filters]', '[data-active-filters]'],
        ['[data-result-count]', '[data-result-count]'],
        ['[data-result-count-mobile]', '[data-result-count-mobile]'],
      ];

      swaps.forEach(([selector]) => {
        const current = root.querySelector(selector);
        const incoming = newSection.querySelector(selector);
        if (current && incoming) current.replaceWith(incoming);
      });

      bindFilterEvents();
      bindCardActionsDirectly();
      renderActiveFilterChips();
      updateActiveFilterCount();
    } catch (err) {
      if (err.name !== 'AbortError') console.error('[Doux] Section render failed:', err);
    } finally {
      root.removeAttribute('aria-busy');
      root.style.opacity = '';
      root.style.pointerEvents = '';
    }
  }

  /* ============================================================
     FILTER FORM
  ============================================================ */

  let filterDebounce = null;

  const collectFormParams = () => {
    const form = root.querySelector('[data-filters-form]');
    if (!form) return new URLSearchParams();
    const fd = new FormData(form);
    const params = new URLSearchParams();
    for (const [key, value] of fd.entries()) {
      if (value !== '' && value != null) params.append(key, value);
    }
    // Preserve current sort
    const sort = root.querySelector('[data-sort-by]')?.value;
    if (sort) params.append('sort_by', sort);
    return params;
  };

  function applyFilters({ debounce = 0 } = {}) {
    clearTimeout(filterDebounce);
    filterDebounce = setTimeout(() => {
      updateURL(collectFormParams());
      renderSection();
    }, debounce);
  }

  function bindFilterEvents() {
    const form = root.querySelector('[data-filters-form]');
    if (!form) return;

    form.addEventListener('change', (e) => {
      if (e.target.matches('input[type="checkbox"]')) {
        applyFilters();
      }
    });

    form.addEventListener('input', (e) => {
      if (e.target.matches('input[type="number"]')) {
        applyFilters({ debounce: 600 });
      }
    });

    // Accordion toggle for filter groups
    form.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-filter-group-toggle]');
      if (!toggle) return;
      e.preventDefault();

      const group = toggle.closest('[data-filter-group]');
      const content = group?.querySelector('.doux-filter-group__content');
      if (!group || !content) return;

      const isOpen = group.classList.contains('is-open');
      if (isOpen) {
        group.classList.remove('is-open');
        content.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        group.classList.add('is-open');
        content.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
      }
    });

    // Clear all
    root.querySelectorAll('[data-clear-all]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const url = getURL();
        [...url.searchParams.keys()].forEach((key) => {
          if (key.startsWith('filter.')) url.searchParams.delete(key);
        });
        window.history.pushState({}, '', url);
        renderSection();
      });
    });

    // Hide "Clear all" when no filters are active
    updateClearAllVisibility();
  }

  function updateClearAllVisibility() {
    const hasActiveFilters =
      root.querySelectorAll('[data-filters-form] input[type="checkbox"]:checked').length > 0 ||
      [...root.querySelectorAll('[data-filters-form] input[type="number"]')].some((i) => i.value);

    root.querySelectorAll('[data-clear-all]').forEach((btn) => {
      btn.hidden = !hasActiveFilters;
    });
  }

  /* ============================================================
     ACTIVE FILTER CHIPS
  ============================================================ */

  function renderActiveFilterChips() {
    const container = root.querySelector('[data-active-filters]');
    if (!container) return;

    const checked = root.querySelectorAll('[data-filters-form] input[type="checkbox"]:checked');
    const priceInputs = root.querySelectorAll('[data-filters-form] input[type="number"]');

    const chips = [];

    checked.forEach((input) => {
      const label = input.closest('label')?.querySelector('.doux-checkbox__label')?.textContent.trim();
      chips.push({ label, action: () => { input.checked = false; applyFilters(); } });
    });

    priceInputs.forEach((input) => {
      if (input.value) {
        const isMin = input.name.includes('price.gte');
        chips.push({
          label: `${isMin ? 'From' : 'Up to'} $${input.value}`,
          action: () => { input.value = ''; applyFilters(); },
        });
      }
    });

    container.innerHTML = '';
    if (chips.length === 0) {
      container.hidden = true;
      return;
    }
    container.hidden = false;

    chips.forEach(({ label, action }) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'doux-chip';
      chip.innerHTML = `<span>${label}</span>
        <span class="doux-chip__remove" aria-hidden="true">
          <svg width="10" height="10" viewBox="0 0 10 10"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
        </span>`;
      chip.setAttribute('aria-label', `Remove ${label} filter`);
      chip.addEventListener('click', action);
      container.appendChild(chip);
    });
  }

  function updateActiveFilterCount() {
    const count = root.querySelectorAll('[data-filters-form] input[type="checkbox"]:checked').length
      + [...root.querySelectorAll('[data-filters-form] input[type="number"]')].filter((i) => i.value).length;
    const badge = root.querySelector('[data-active-filter-count]');
    if (badge) {
      if (count > 0) {
        badge.textContent = count;
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }
    updateClearAllVisibility();
  }

  /* ============================================================
     SORT
  ============================================================ */

  function bindSort() {
    root.querySelector('[data-sort-by]')?.addEventListener('change', () => {
      const params = collectFormParams();
      // collectFormParams already added sort, but ensure it's the new one
      updateURL(params);
      renderSection();
    });
  }

  /* ============================================================
     MOBILE FILTER DRAWER
  ============================================================ */

  function openFilterDrawer() {
    const sidebar = root.querySelector('[data-filters-sidebar]');
    const overlay = root.querySelector('[data-filter-overlay]');
    const toggle = root.querySelector('[data-filter-toggle]');
    if (!sidebar) return;

    sidebar.setAttribute('data-open', 'true');
    if (overlay) overlay.setAttribute('data-open', 'true');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeFilterDrawer() {
    const sidebar = root.querySelector('[data-filters-sidebar]');
    const overlay = root.querySelector('[data-filter-overlay]');
    const toggle = root.querySelector('[data-filter-toggle]');
    if (!sidebar) return;

    sidebar.setAttribute('data-open', 'false');
    if (overlay) overlay.setAttribute('data-open', 'false');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  function bindMobileFilterToggle() {
    const toggle = root.querySelector('[data-filter-toggle]');
    const overlay = root.querySelector('[data-filter-overlay]');

    // Ensure drawer starts closed (defensive)
    closeFilterDrawer();

    if (toggle) {
      toggle.addEventListener('click', () => {
        const sidebar = root.querySelector('[data-filters-sidebar]');
        const isOpen = sidebar?.getAttribute('data-open') === 'true';
        if (isOpen) closeFilterDrawer();
        else openFilterDrawer();
      });
    }

    // Close on overlay click
    if (overlay) {
      overlay.addEventListener('click', closeFilterDrawer);
    }

    // Close on Esc
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const sidebar = root.querySelector('[data-filters-sidebar]');
        if (sidebar?.getAttribute('data-open') === 'true') closeFilterDrawer();
      }
    });
  }

  // Delegated close (works after section re-renders)
  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-filter-close]')) {
      e.preventDefault();
      closeFilterDrawer();
    }
  });

  /* ============================================================
     CART DRAWER INTEGRATION
     Hot-swap #header-minicart-drawer, then trigger [data-site-actions-cart]
  ============================================================ */

  async function refreshCartDrawer() {
    try {
      const res = await fetch(`${window.location.pathname}?section_id=header-minicart`, {
        headers: { Accept: 'text/html' },
      });
      if (!res.ok) {
        // Try alternate section name
        const fallback = await fetch(`${window.location.pathname}?sections=header-minicart`);
        if (fallback.ok) {
          const data = await fallback.json();
          const html = data['header-minicart'];
          if (html) {
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            const incoming = tmp.querySelector('#header-minicart-drawer');
            const current = document.querySelector('#header-minicart-drawer');
            if (incoming && current) current.replaceWith(incoming);
          }
        }
        return;
      }
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.querySelector('#header-minicart-drawer');
      const current = document.querySelector('#header-minicart-drawer');
      if (incoming && current) current.replaceWith(incoming);
    } catch (err) {
      console.warn('[Doux] Cart drawer refresh failed:', err);
    }
  }

  function openCartDrawer() {
    const trigger = document.querySelector('[data-site-actions-cart]');
    if (trigger) {
      trigger.click();
    } else {
      // Fallback: dispatch event the theme might listen for
      document.dispatchEvent(new CustomEvent('cart:open'));
    }
  }

  /* ============================================================
     QUICK ADD (single-variant products)
  ============================================================ */

  async function quickAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const labelEl = button.querySelector('[data-btn-label]');
    const loader = button.querySelector('.doux-btn__loader');
    const originalLabel = labelEl?.textContent;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (loader) loader.hidden = false;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }

      // Refresh drawer, then open
      await refreshCartDrawer();
      if (loader) loader.hidden = true;
      button.classList.add('doux-btn--success');
      if (labelEl) labelEl.textContent = 'Added';
      button.removeAttribute('aria-busy');

      openCartDrawer();

      // Reset button after a beat
      setTimeout(() => {
        button.classList.remove('doux-btn--success');
        if (labelEl) labelEl.textContent = originalLabel || 'Add To Cart';
        button.disabled = false;
      }, 1400);

      // Dispatch event for any other listeners
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      console.error('[Doux] Quick add failed:', err);
      if (loader) loader.hidden = true;
      if (labelEl) labelEl.textContent = 'Try Again';
      button.removeAttribute('aria-busy');
      button.disabled = false;
      setTimeout(() => {
        if (labelEl) labelEl.textContent = originalLabel || 'Add To Cart';
      }, 1800);
    }
  }

  /* ============================================================
     QUICK VIEW (multi-variant products)
  ============================================================ */

  // Lazily resolve the modal — handles async DOM and section re-renders
  function getQuickView() {
    return document.querySelector('[data-quickview]');
  }
  function getQuickViewContent() {
    return getQuickView()?.querySelector('[data-quickview-content]');
  }

  // Move the quickview modal to be a direct child of <body> so position:fixed works
  // regardless of any ancestor's transform/filter/will-change properties that would
  // otherwise create a new containing block.
  function relocateQuickViewToBody() {
    const qv = getQuickView();
    if (qv && qv.parentElement !== document.body) {
      document.body.appendChild(qv);
    }
  }
  relocateQuickViewToBody();

  async function openQuickView(button) {
    const handle = button.dataset.productHandle;
    const productUrl = button.dataset.productUrl;

    if (!handle) {
      console.warn('[Doux] Quick view: missing product handle');
      return;
    }

    // Remove any existing modal first
    document.querySelectorAll('.doux-qv-modal-fresh').forEach(m => m.remove());

    // Build a brand-new modal with everything inline — ignores theme CSS entirely
    const modal = document.createElement('div');
    modal.className = 'doux-qv-modal-fresh';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      z-index: 2147483647 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      background: rgba(0, 0, 0, 0.65) !important;
      padding: 20px !important;
      box-sizing: border-box !important;
      animation: douxFadeIn 0.2s ease !important;
    `;

    modal.innerHTML = `
      <style>
        @keyframes douxFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes douxSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      </style>
      <div class="doux-qv-fresh-panel" style="
        position: relative;
        background: white;
        width: 100%;
        max-width: 880px;
        max-height: 90vh;
        overflow-y: auto;
        animation: douxSlideUp 0.25s ease;
        font-family: 'GT America', 'Helvetica Neue', Arial, sans-serif;
        color: #0A0A0A;
      ">
        <button class="doux-qv-fresh-close" type="button" style="
          position: absolute;
          top: 12px;
          right: 12px;
          width: 36px;
          height: 36px;
          background: white;
          border: 1.5px solid #0A0A0A;
          cursor: pointer;
          z-index: 10;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 0;
        " aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="2" fill="none"/></svg>
        </button>
        <div class="doux-qv-fresh-content" style="padding: 40px; min-height: 200px;">
          <div style="text-align: center; padding: 40px; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; font-weight: 700; color: #888;">Loading…</div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    // Close handlers
    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.closest('.doux-qv-fresh-close')) {
        modal.remove();
        document.body.style.overflow = '';
      }
    });
    const escClose = (e) => {
      if (e.key === 'Escape') {
        modal.remove();
        document.body.style.overflow = '';
        document.removeEventListener('keydown', escClose);
      }
    };
    document.addEventListener('keydown', escClose);

    // Fetch product and render
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Product fetch failed');
      const product = await res.json();
      renderQuickViewFresh(modal, product, productUrl);
    } catch (err) {
      console.error('[Doux] Quick view failed:', err);
      const content = modal.querySelector('.doux-qv-fresh-content');
      if (content) {
        content.innerHTML = `<p style="padding:20px;text-align:center;">Could not load this product. <a href="${productUrl}" style="color:#0A0A0A;">View full details</a>.</p>`;
      }
    }
  }

  function renderQuickViewFresh(modal, product, productUrl) {
    const content = modal.querySelector('.doux-qv-fresh-content');
    if (!content) return;

    const firstVariant = product.variants.find((v) => v.available) || product.variants[0];

    // Images
    const images = (product.media && product.media.length > 0)
      ? product.media.filter((m) => m.media_type === 'image').map((m) => m.src || m.preview_image?.src).filter(Boolean)
      : (product.images && product.images.length > 0) ? product.images : [product.featured_image].filter(Boolean);

    // Thumbnails HTML
    const thumbsHTML = images.length > 1 ? `
      <div style="display:flex;gap:8px;overflow-x:auto;margin-top:12px;">
        ${images.map((src, idx) => `
          <button type="button" class="doux-qv-fresh-thumb" data-image="${escapeHtml(src)}" aria-pressed="${idx === 0}" style="
            flex:none;width:56px;height:56px;background:#F7F7F7;
            border:1.5px solid ${idx === 0 ? '#0A0A0A' : 'transparent'};
            cursor:pointer;padding:0;overflow:hidden;border-radius:0;">
            <img src="${escapeHtml(src)}" style="width:100%;height:100%;object-fit:cover;display:block;" alt="">
          </button>
        `).join('')}
      </div>` : '';

    // Options HTML — Shopify product.js returns product.options as array of objects {name, position, values}
    // Hide the option block entirely if the product is single-variant (Shopify auto-creates "Title: Default Title")
    const hasRealOptions = product.has_only_default_variant === false
      || (product.variants.length > 1)
      || (product.options.length > 0 && !(product.options.length === 1 && (
          (typeof product.options[0] === 'string' && product.options[0].toLowerCase() === 'title')
          || (product.options[0]?.name && product.options[0].name.toLowerCase() === 'title')
        )));

    const optionsHTML = !hasRealOptions ? '' : product.options.map((option, idx) => {
      // Shopify returns options as either strings (older API) or objects {name, values}
      const optionName = typeof option === 'string' ? option : option.name;
      const values = [...new Set(product.variants.map((v) => v.options[idx]))];
      return `
        <div style="margin-bottom:16px;" data-option-group data-option-index="${idx}">
          <label style="font-size:10px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;display:block;margin-bottom:8px;color:#444;">${escapeHtml(optionName)}</label>
          <div style="display:flex;flex-wrap:wrap;gap:8px;">
            ${values.map((val) => {
              const isActive = firstVariant.options[idx] === val;
              return `<button type="button" data-option-value="${escapeHtml(val)}" aria-pressed="${isActive}" style="
                padding:8px 14px;
                border:1.5px solid ${isActive ? '#0A0A0A' : '#CCC'};
                background:white;
                color:#0A0A0A;
                font-family:inherit;font-size:11px;font-weight:700;
                letter-spacing:0.08em;text-transform:uppercase;
                cursor:pointer;border-radius:0;
                transition:border-color 0.15s ease;">${escapeHtml(val)}</button>`;
            }).join('')}
          </div>
        </div>`;
    }).join('');

    // Price HTML
    const priceHTML = firstVariant.compare_at_price && firstVariant.compare_at_price > firstVariant.price
      ? `<span style="color:#0EBEB6;">${formatMoney(firstVariant.price)}</span>
         <span style="color:#0EBEB6;text-decoration:line-through;font-weight:400;font-size:14px;margin-left:8px;opacity:0.65;">${formatMoney(firstVariant.compare_at_price)}</span>`
      : `<span>${formatMoney(firstVariant.price)}</span>`;

    // Parse description into tabs
    const sections = parseDouxSections(product.description || '');

    const hasIngredients = !!sections.ingredients;
    const hasUsage = !!sections.usage;

    const tabsHTML = `
      <div style="margin-top:24px;border-top:1px solid #EEE;">
        <div style="display:flex;border-bottom:1px solid #EEE;" data-tab-buttons>
          <button type="button" data-tab="description" aria-selected="true" style="
            flex:1;padding:14px 12px;background:none;border:none;
            border-bottom:2px solid #10C3BB;
            font-family:inherit;font-size:11px;font-weight:800;
            letter-spacing:0.08em;text-transform:uppercase;cursor:pointer;color:#0A0A0A;
            margin-bottom:-1px;">Description</button>
          ${hasIngredients ? `<button type="button" data-tab="ingredients" aria-selected="false" style="
            flex:1;padding:14px 12px;background:none;border:none;
            border-bottom:2px solid transparent;
            font-family:inherit;font-size:11px;font-weight:800;
            letter-spacing:0.08em;text-transform:uppercase;cursor:pointer;color:#888;
            margin-bottom:-1px;">What's Good</button>` : ''}
          ${hasUsage ? `<button type="button" data-tab="usage" aria-selected="false" style="
            flex:1;padding:14px 12px;background:none;border:none;
            border-bottom:2px solid transparent;
            font-family:inherit;font-size:11px;font-weight:800;
            letter-spacing:0.08em;text-transform:uppercase;cursor:pointer;color:#888;
            margin-bottom:-1px;">How To Use</button>` : ''}
        </div>
        <div style="padding:20px 0 4px;font-size:13px;line-height:1.6;color:#444;" data-tab-panels>
          <div data-panel="description">${sections.description || product.description}</div>
          ${hasIngredients ? `<div data-panel="ingredients" style="display:none;">${sections.ingredients}</div>` : ''}
          ${hasUsage ? `<div data-panel="usage" style="display:none;">${sections.usage}</div>` : ''}
        </div>
      </div>`;

    // Main markup
    content.innerHTML = `
      <div style="display:grid;grid-template-columns:1fr;gap:24px;" data-qv-grid>
        <div>
          <div style="position:relative;width:100%;aspect-ratio:1/1;background:#F7F7F7;overflow:hidden;">
            <img src="${escapeHtml(images[0] || product.featured_image)}" data-main-image style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;" alt="${escapeHtml(product.title)}">
          </div>
          ${thumbsHTML}
        </div>
        <div style="display:flex;flex-direction:column;">
          <h2 style="font-size:22px;font-weight:900;text-transform:uppercase;letter-spacing:0.04em;line-height:1.1;margin:0 0 8px;">${escapeHtml(product.title)}</h2>
          <div style="font-size:16px;font-weight:700;margin-bottom:20px;" data-price>${priceHTML}</div>
          ${optionsHTML}
          <button type="button" data-add-btn data-variant-id="${firstVariant.id}" style="
            width:100%;padding:14px 20px;background:#0A0A0A;color:white;
            border:1.5px solid #0A0A0A;border-radius:0;
            font-family:inherit;font-size:11px;font-weight:800;
            letter-spacing:0.08em;text-transform:uppercase;cursor:pointer;margin-top:4px;
            display:inline-flex;align-items:center;justify-content:center;gap:8px;">
            <span data-btn-label>${firstVariant.available ? 'Add To Cart' : 'Sold Out'}</span>
          </button>
          <a href="${escapeHtml(productUrl)}" style="
            display:inline-block;margin-top:16px;align-self:flex-start;
            font-family:inherit;font-size:11px;font-weight:700;
            letter-spacing:0.08em;text-transform:uppercase;color:#888;
            text-decoration:underline;text-underline-offset:3px;">View Full Details →</a>
          ${tabsHTML}
        </div>
      </div>
    `;

    // Apply responsive grid via JS
    if (window.innerWidth >= 750) {
      content.querySelector('[data-qv-grid]').style.gridTemplateColumns = '1fr 1fr';
      content.querySelector('[data-qv-grid]').style.gap = '40px';
    }

    bindFreshQuickViewEvents(modal, product);
  }

  function bindFreshQuickViewEvents(modal, product) {
    // Thumbnail clicks
    const mainImage = modal.querySelector('[data-main-image]');
    modal.querySelectorAll('.doux-qv-fresh-thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        modal.querySelectorAll('.doux-qv-fresh-thumb').forEach(t => {
          t.setAttribute('aria-pressed', 'false');
          t.style.borderColor = 'transparent';
        });
        thumb.setAttribute('aria-pressed', 'true');
        thumb.style.borderColor = '#0A0A0A';
        if (mainImage) mainImage.src = thumb.dataset.image;
      });
    });

    // Tab clicks
    const tabButtons = modal.querySelectorAll('[data-tab]');
    const tabPanels = modal.querySelectorAll('[data-panel]');
    tabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        tabButtons.forEach(b => {
          b.setAttribute('aria-selected', 'false');
          b.style.color = '#888';
          b.style.borderBottomColor = 'transparent';
        });
        btn.setAttribute('aria-selected', 'true');
        btn.style.color = '#0A0A0A';
        btn.style.borderBottomColor = '#10C3BB';

        const target = btn.dataset.tab;
        tabPanels.forEach(p => {
          p.style.display = p.dataset.panel === target ? '' : 'none';
        });
      });
    });

    // Variant picker
    const addBtn = modal.querySelector('[data-add-btn]');
    const priceEl = modal.querySelector('[data-price]');
    const selected = [];
    product.options.forEach((_, i) => {
      const pressed = modal.querySelectorAll('[data-option-group]')[i]?.querySelector('[aria-pressed="true"]');
      selected[i] = pressed?.dataset.optionValue || product.variants[0].options[i];
    });

    function findVariant() {
      return product.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
    }

    function updateUI() {
      const variant = findVariant();
      const labelEl = addBtn.querySelector('[data-btn-label]');

      if (!variant) {
        addBtn.disabled = true;
        addBtn.style.opacity = '0.5';
        if (labelEl) labelEl.textContent = 'Unavailable';
        return;
      }

      addBtn.dataset.variantId = variant.id;
      addBtn.disabled = !variant.available;
      addBtn.style.opacity = variant.available ? '1' : '0.5';
      if (labelEl) labelEl.textContent = variant.available ? 'Add To Cart' : 'Sold Out';

      if (priceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceEl.innerHTML = `<span style="color:#0EBEB6;">${formatMoney(variant.price)}</span>
            <span style="color:#0EBEB6;text-decoration:line-through;font-weight:400;font-size:14px;margin-left:8px;opacity:0.65;">${formatMoney(variant.compare_at_price)}</span>`;
        } else {
          priceEl.innerHTML = `<span>${formatMoney(variant.price)}</span>`;
        }
      }

      if (mainImage && variant.featured_image && variant.featured_image.src) {
        mainImage.src = variant.featured_image.src;
      }
    }

    modal.querySelectorAll('[data-option-group]').forEach((group, i) => {
      group.querySelectorAll('[data-option-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('[data-option-value]').forEach((b) => {
            b.setAttribute('aria-pressed', 'false');
            b.style.borderColor = '#CCC';
          });
          btn.setAttribute('aria-pressed', 'true');
          btn.style.borderColor = '#0A0A0A';
          selected[i] = btn.dataset.optionValue;
          updateUI();
        });
      });
    });

    // Add to cart
    addBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      const variantId = addBtn.dataset.variantId;
      const labelEl = addBtn.querySelector('[data-btn-label]');
      const originalLabel = labelEl?.textContent;
      addBtn.disabled = true;
      if (labelEl) labelEl.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('Add failed');
        await refreshCartDrawer();
        if (labelEl) labelEl.textContent = 'Added';
        modal.remove();
        document.body.style.overflow = '';
        openCartDrawer();
      } catch (err) {
        if (labelEl) labelEl.textContent = 'Try Again';
        addBtn.disabled = false;
        setTimeout(() => { if (labelEl) labelEl.textContent = originalLabel || 'Add To Cart'; }, 1800);
      }
    });
  }

  function parseDouxSections(html) {
    const result = { description: null, ingredients: null, usage: null };

    // Create a temporary DOM to parse the HTML
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.innerHTML;

    // Try to extract sections using flexible matching for headers
    const ingredientsMatch = text.match(/(WHAT['']?S\s+GOOD|INGREDIENTS|KEY\s+INGREDIENTS)[\s\S]*?(?=(DOUX\s+YOU|HOW\s+TO\s+USE|DIRECTIONS|<h[1-6])|$)/i);
    const usageMatch = text.match(/(DOUX\s+YOU|HOW\s+TO\s+USE|DIRECTIONS)[\s\S]*?(?=(WHAT['']?S\s+GOOD|<h[1-6])|$)/i);
    const descMatch = text.match(/(WHAT\s+IT\s+DOUX[\?]?|DESCRIPTION)[\s\S]*?(?=(WHAT['']?S\s+GOOD|DOUX\s+YOU|HOW\s+TO\s+USE|<h[1-6])|$)/i);

    if (descMatch) result.description = descMatch[0];
    if (ingredientsMatch) result.ingredients = ingredientsMatch[0];
    if (usageMatch) result.usage = usageMatch[0];

    // If nothing matched, just use the whole description for the description tab
    if (!result.description && !result.ingredients && !result.usage) {
      result.description = html;
    }

    return result;
  }

  function closeQuickView() {
    const quickview = getQuickView();
    if (!quickview) return;
    quickview.hidden = true;
    quickview.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* ============================================================
     EVENT DELEGATION (works after DOM swaps)
  ============================================================ */

  // Direct per-button binding for Quick View / Quick Add — bypasses any theme script
  // that might be calling stopImmediatePropagation() on document-level handlers
  function bindCardActionsDirectly() {
    document.querySelectorAll('[data-quick-view]').forEach((btn) => {
      if (btn.__douxBound) return;
      btn.__douxBound = true;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openQuickView(btn);
      });
    });

    document.querySelectorAll('[data-quick-add]').forEach((btn) => {
      if (btn.__douxBound) return;
      btn.__douxBound = true;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        quickAdd(btn);
      });
    });

    // Re-render BazaarVoice inline ratings on newly-injected cards
    // (after filter change or Show More). Uses polling so BV catches up reliably
    // even when widgets are added after initial page load.
    waitForBazaarVoiceAndRender();
  }

  function refreshBazaarVoice() {
    // BV's modern Conversations SDK auto-renders inline_ratings widgets via its own
    // mutation observer (BV.registerMutationObserver). No manual API call needed —
    // widgets that have been added to the DOM get processed automatically.
    //
    // The previous BV.ui('inline_ratings', 'show_reviews') call threw "Scope not supported"
    // because that scope/method combination is from the legacy $BV.ui('rr') API,
    // not Conversations. We're letting BV handle it natively now.
    //
    // This function is kept as a no-op for compatibility with callers, but may be
    // extended in the future if BV adds a public re-render API.
  }

  // Polling kept for diagnostic purposes — BV's mutation observer handles render automatically,
  // but we wait for it to finish so we know when widgets are ready.
  function waitForBazaarVoiceAndRender(attempts = 0) {
    const MAX_ATTEMPTS = 40;
    const reviewDivs = document.querySelectorAll('[data-bv-show="inline_rating"]');
    const rendered = [...reviewDivs].filter((d) => d.dataset.bvReady === 'true').length;

    // Stop polling once BV has processed at least half the widgets (the rest may legitimately have no reviews)
    const enoughProcessed = reviewDivs.length === 0 || rendered >= Math.ceil(reviewDivs.length * 0.5);

    if (enoughProcessed) {
      // Hide skeleton shimmer for all widgets that BV has marked ready, including
      // those with no review data (so the shimmer doesn't loop forever on unreviewed products)
      reviewDivs.forEach((div) => {
        if (div.dataset.bvReady === 'true') {
          const wrapper = div.closest('.doux-card__reviews');
          if (wrapper) wrapper.classList.add('is-bv-processed');
        }
      });
      return;
    }

    if (attempts < MAX_ATTEMPTS) {
      setTimeout(() => waitForBazaarVoiceAndRender(attempts + 1), 300);
    } else {
      // Hit max polling attempts — hide all shimmers anyway so unreviewed/slow products
      // don't show infinite loading state
      reviewDivs.forEach((div) => {
        const wrapper = div.closest('.doux-card__reviews');
        if (wrapper) wrapper.classList.add('is-bv-processed');
      });
    }
  }

  // Bind on init and re-bind after section renders (filters / Show More append new cards)
  bindCardActionsDirectly();

  // Kick off BV polling loop on page load
  waitForBazaarVoiceAndRender();

  // Capture-phase delegation for elements the theme/Sentry interferes with:
  // - Anchor link navigation (image, title, View Details on sold-out)
  // - Filter checkboxes (Bespoke theme may be hijacking the change event)
  root.addEventListener('click', (e) => {
    // Force navigation on product card anchor links
    const navLink = e.target.closest('.doux-card__media-link, .doux-card__title a, a.doux-card__action');
    if (navLink && navLink.href) {
      // Don't hijack right-click, middle-click, modifier keys (open in new tab, etc.)
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      e.stopPropagation();
      window.location.href = navLink.href;
      return;
    }

    // Show More button — uses delegation since the button itself gets replaced after each load
    const loadMoreBtn = e.target.closest('[data-load-more]');
    if (loadMoreBtn) {
      e.preventDefault();
      loadMoreProducts(loadMoreBtn);
      return;
    }

    // Filter checkbox — clicks on the visual label/wrapper need to toggle the input
    // and we re-apply filters. Capture phase ensures we beat any theme interference.
    const checkboxLabel = e.target.closest('.doux-checkbox');
    if (checkboxLabel) {
      e.stopPropagation();
      // Let the browser do its native checkbox toggle (don't preventDefault), then react.
      // Schedule on next tick so the checked state has flipped.
      setTimeout(() => applyFilters(), 0);
      return;
    }
  }, true); // CAPTURE PHASE

  /* ============================================================
     SHOW MORE / LOAD MORE
  ============================================================ */

  let loadMoreInFlight = false;

  async function loadMoreProducts(btn) {
    if (loadMoreInFlight) return;
    const wrap = btn.closest('[data-load-more-wrap]');
    if (!wrap) return;

    const nextUrl = wrap.dataset.nextUrl;
    const currentPage = parseInt(wrap.dataset.currentPage, 10) || 1;
    const totalPages = parseInt(wrap.dataset.totalPages, 10) || 1;

    if (!nextUrl || currentPage >= totalPages) return;

    loadMoreInFlight = true;
    const labelEl = btn.querySelector('[data-load-more-label]');
    const loader = btn.querySelector('.doux-btn__loader');
    const originalLabel = labelEl?.textContent;

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    if (loader) loader.hidden = false;

    try {
      // Fetch the next page rendered through the section
      const fetchUrl = new URL(nextUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', SECTION_ID);

      const res = await fetch(fetchUrl.toString());
      if (!res.ok) throw new Error('Load more fetch failed');

      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      // Get the new product cards from the fetched section
      const newGrid = parsed.querySelector('[data-product-grid]');
      const newWrap = parsed.querySelector('[data-load-more-wrap]');

      if (newGrid) {
        // Append all new product cards (everything except the load-more wrapper) into our existing grid
        const existingGrid = root.querySelector('[data-product-grid]');
        const existingWrap = root.querySelector('[data-load-more-wrap]');

        // Move all new product cards (children that aren't the load-more wrapper) into the existing grid
        const newCards = newGrid.querySelectorAll('[data-product-card]');
        newCards.forEach((card) => {
          existingGrid.insertBefore(card, existingWrap);
        });

        // Update the wrapper with new page info
        if (newWrap && existingWrap) {
          existingWrap.dataset.currentPage = newWrap.dataset.currentPage;
          existingWrap.dataset.totalPages = newWrap.dataset.totalPages;
          existingWrap.dataset.nextUrl = newWrap.dataset.nextUrl || '';

          // Update counts and progress bar
          const newLoaded = newWrap.querySelector('[data-loaded-count]')?.textContent;
          const newTotal = newWrap.querySelector('[data-total-count]')?.textContent;
          const newFillPct = newWrap.querySelector('[data-progress-fill]')?.style.width;

          if (newLoaded) {
            const loadedEl = existingWrap.querySelector('[data-loaded-count]');
            if (loadedEl) {
              // Sum the count rather than overwrite (since paginate restarts count per page)
              const previouslyLoaded = parseInt(loadedEl.textContent, 10) || 0;
              const additionallyLoaded = newCards.length;
              loadedEl.textContent = previouslyLoaded + additionallyLoaded;
            }
          }

          // Update progress bar based on summed count
          const loadedCount = parseInt(existingWrap.querySelector('[data-loaded-count]')?.textContent, 10) || 0;
          const totalCount = parseInt(existingWrap.querySelector('[data-total-count]')?.textContent, 10) || 1;
          const fillEl = existingWrap.querySelector('[data-progress-fill]');
          if (fillEl) fillEl.style.width = `${Math.min(100, (loadedCount / totalCount) * 100)}%`;

          // If we've loaded everything, hide the Show More button
          const updatedPage = parseInt(existingWrap.dataset.currentPage, 10);
          const updatedTotal = parseInt(existingWrap.dataset.totalPages, 10);
          if (updatedPage >= updatedTotal || !existingWrap.dataset.nextUrl) {
            btn.remove();
          }
        }

        // Bind handlers to new cards (Quick View / Quick Add)
        bindCardActionsDirectly();
      }
    } catch (err) {
      console.error('[Doux] Load more failed:', err);
      if (labelEl) labelEl.textContent = 'Try Again';
    } finally {
      if (loader) loader.hidden = true;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      if (labelEl && labelEl.textContent !== 'Try Again') {
        labelEl.textContent = originalLabel || 'Show More';
      }
      loadMoreInFlight = false;
    }
  }

  // Use document-level delegation for quickview close (handles modal re-mounting)
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-quickview-close]')) {
      closeQuickView();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const qv = getQuickView();
      if (qv && !qv.hidden) closeQuickView();
    }
  });

  /* ============================================================
     BROWSER BACK/FORWARD
  ============================================================ */

  window.addEventListener('popstate', () => renderSection());

  /* ============================================================
     UTILITIES
  ============================================================ */

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    // Shopify provides window.Shopify.formatMoney in many themes; fall back to simple formatting
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  /* ============================================================
     INIT
  ============================================================ */

  console.log('[Doux] Collection grid initialized');

  bindFilterEvents();
  bindSort();
  bindMobileFilterToggle();
  renderActiveFilterChips();
  updateActiveFilterCount();
})();